import { emit, on } from '@lucid/core';

const DRAWER_ID = 'size-guide';
const norm = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

let pendingTab = '';

function findDrawer(id = DRAWER_ID) {
  return document.querySelector(`:is(theme-drawer, lucid-drawer)[data-lucid-drawer="${id}"]`);
}

/* Products with several size options (tracksuit sets) get one drawer per piece: size-guide-1, size-guide-2. The number
   is the 1-based option of the variant picker that drawer belongs to; 0 means the whole picker. */
const isGuideId = (id) => id === DRAWER_ID || String(id || '').startsWith(`${DRAWER_ID}-`);
const optionOf = (id) => (String(id || '').startsWith(`${DRAWER_ID}-`) ? Number(String(id).slice(DRAWER_ID.length + 1)) || 0 : 0);

function pickerScope(option) {
  const picker = mainPicker();
  if (!picker || !option) return picker;
  return picker.querySelector(`fieldset[data-fieldset-index="${option - 1}"]`) || picker.querySelectorAll('fieldset')[option - 1] || picker;
}

/** The variant picker of the product page itself, not of a card or the quick add dialog. */
function mainPicker() {
  return Array.from(document.querySelectorAll('variant-picker')).find(
    (picker) => !picker.closest('product-card, quick-add-component, dialog, .lucid-card')
  );
}

function selectedValues(option = 0) {
  const picker = pickerScope(option);
  if (!picker) return [];
  const values = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).map((input) => input.value);
  picker.querySelectorAll('select').forEach((select) => {
    const option = select.selectedOptions[0];
    if (option) values.push(option.textContent?.trim() || option.value);
  });
  return values.map(norm);
}

function findSizeControl(size, option = 0) {
  const picker = pickerScope(option);
  if (!picker) return null;
  const wanted = norm(size);
  const input = Array.from(picker.querySelectorAll('input[type="radio"]')).find((item) => norm(item.value) === wanted);
  if (input) return { kind: 'input', element: input, soldOut: input.getAttribute('aria-disabled') === 'true' };
  for (const select of picker.querySelectorAll('select')) {
    const option = Array.from(select.options).find((item) => norm(item.textContent) === wanted || norm(item.value) === wanted);
    if (option) return { kind: 'select', element: select, option, soldOut: option.disabled || /ausverkauft|sold out/i.test(option.textContent || '') };
  }
  return null;
}

function applySize(size, option = 0) {
  const control = findSizeControl(size, option);
  if (!control) return false;
  if (control.kind === 'input') {
    if (!control.element.checked) control.element.click();
  } else if (control.element.value !== control.option.value) {
    control.element.value = control.option.value;
    control.element.dispatchEvent(new Event('change', { bubbles: true }));
  }
  emit('size-guide:apply', { size });
  return true;
}

function parseRows(text) {
  const rows = {};
  String(text || '')
    .split(/\n|;/)
    .forEach((line) => {
      const match = line.match(/^\s*([A-Za-z])\s*[:=]\s*([^|]+?)\s*(?:\|\s*(.+?))?\s*$/);
      if (match) rows[match[1].toUpperCase()] = [match[2], match[3] || ''];
    });
  return rows;
}

class LucidSizeGuide extends HTMLElement {
  get #option() {
    return optionOf(this.closest('[data-lucid-drawer]')?.getAttribute('data-lucid-drawer'));
  }

  #chart = null;
  #height = -1;
  #weight = -1;
  #offs = [];

  connectedCallback() {
    this.#chart = this.#findChart();
    this.#enhanceTable();
    this.#buildFinder();
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-lucid-size-tabs]')?.addEventListener('keydown', this.#onTabKey);
    this.#offs.push(
      on('product:variant', () => this.highlight()),
      on('drawer:state', (event) => {
        const own = this.closest('[data-lucid-drawer]')?.getAttribute('data-lucid-drawer');
        if (!isGuideId(event.detail?.id) || event.detail.id !== own || !event.detail.open) return;
        this.showTab(pendingTab || (this.hasFinder ? 'finder' : 'table'));
        pendingTab = '';
        this.highlight();
      })
    );
    this.highlight();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#offs.forEach((off) => off());
    this.#offs = [];
  }

  get hasFinder() {
    const chart = this.#chart;
    return Boolean(chart && Array.isArray(chart.heights) && Array.isArray(chart.weights) && Array.isArray(chart.sizes) && chart.sizes.length);
  }

  #findChart() {
    let charts = [];
    try {
      charts = JSON.parse(this.querySelector('[data-lucid-size-charts]')?.textContent || '[]');
    } catch (error) {
      console.warn('Size finder data is not valid JSON', error);
      return null;
    }
    if (!Array.isArray(charts)) charts = [charts];
    const haystack = (this.dataset.match || '').toLowerCase();
    if (!haystack.trim()) return null;
    return (
      charts.find((chart) => {
        const words = Array.isArray(chart?.match) ? chart.match : [chart?.match];
        const needs = Array.isArray(chart?.requires) ? chart.requires : chart?.requires ? [chart.requires] : [];
        if (!needs.every((word) => haystack.includes(String(word).toLowerCase()))) return false;
        return words.some((word) => word && haystack.includes(String(word).toLowerCase()));
      }) || null
    );
  }

  #enhanceTable() {
    const table = this.querySelector('[data-size-table] table');
    if (!table) return;
    const rows = Object.keys(parseRows(this.dataset.rows)).length ? parseRows(this.dataset.rows) : this.#chart?.rows || {};

    const head = table.tHead?.rows[0] || table.rows[0];
    if (head) {
      Array.from(head.cells).forEach((cell, index) => {
        const text = cell.textContent?.trim() || '';
        if (index === 0) {
          if (this.dataset.textMeasure) cell.textContent = this.dataset.textMeasure;
          return;
        }
        cell.dataset.size = norm(text);
        const parts = text.split('/').map((part) => part.trim());
        if (parts.length === 2 && parts[0] && parts[1]) {
          cell.textContent = parts[0];
          const small = document.createElement('small');
          small.textContent = parts[1];
          cell.append(small);
        }
      });
    }

    Array.from(table.tBodies[0]?.rows || []).forEach((row) => {
      const cell = row.cells[0];
      const key = cell?.textContent?.trim() || '';
      const label = rows[key] || rows[key.toUpperCase()];
      if (!cell || !/^[A-Za-z]$/.test(key) || !label) return;
      cell.textContent = '';
      const badge = document.createElement('span');
      badge.className = 'lucid-size-guide__key';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = key;
      cell.append(badge, label[0]);
      if (label[1]) {
        const note = document.createElement('small');
        note.textContent = label[1];
        cell.append(note);
      }
      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = ` (${key})`;
      cell.append(hidden);
    });
  }

  #buildFinder() {
    if (!this.hasFinder) return;
    const chart = this.#chart;
    const build = (group, labels, name) => {
      if (!group) return;
      group.replaceChildren(
        ...labels.map((label, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'lucid-size-guide__option';
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', 'false');
          button.tabIndex = index === 0 ? 0 : -1;
          button.dataset[name] = String(index);
          button.textContent = label;
          return button;
        })
      );
      group.addEventListener('keydown', this.#onRadioKey);
    };
    build(this.querySelector('[data-finder="height"]'), chart.heights, 'height');
    build(this.querySelector('[data-finder="weight"]'), chart.weights, 'weight');
    const tabs = this.querySelector('[data-lucid-size-tabs]');
    if (tabs) tabs.hidden = false;
    this.showTab('finder');
  }

  showTab(name) {
    const tab = name === 'finder' && this.hasFinder ? 'finder' : 'table';
    this.querySelectorAll('[data-tab]').forEach((button) => {
      const active = button.getAttribute('data-tab') === tab;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    this.querySelector('[data-lucid-size-tabs]')?.classList.toggle('is-second', tab === 'table');
    this.querySelectorAll('[data-pane]').forEach((pane) => {
      pane.hidden = pane.getAttribute('data-pane') !== tab;
    });
  }

  highlight() {
    const table = this.querySelector('[data-size-table] table');
    if (!table) return;
    const head = table.tHead?.rows[0] || table.rows[0];
    if (!head) return;
    const values = selectedValues(this.#option);
    const column = Array.from(head.cells).findIndex((cell, index) => index > 0 && values.includes(cell.dataset.size || norm(cell.textContent)));
    Array.from(table.rows).forEach((row) => {
      Array.from(row.cells).forEach((cell, index) => cell.classList.toggle('is-selected', column > 0 && index === column));
    });
  }

  #updateFinder() {
    this.querySelectorAll('[data-height]').forEach((button) => {
      const on = Number(button.getAttribute('data-height')) === this.#height;
      button.setAttribute('aria-checked', String(on));
      if (this.#height > -1) button.tabIndex = on ? 0 : -1;
    });
    this.querySelectorAll('[data-weight]').forEach((button) => {
      const on = Number(button.getAttribute('data-weight')) === this.#weight;
      button.setAttribute('aria-checked', String(on));
      if (this.#weight > -1) button.tabIndex = on ? 0 : -1;
    });

    const result = this.querySelector('[data-finder-result]');
    const apply = this.querySelector('[data-finder-apply]');
    if (!(result instanceof HTMLElement) || !(apply instanceof HTMLButtonElement)) return;

    const size = this.#height > -1 && this.#weight > -1 ? this.#chart.sizes[this.#height]?.[this.#weight] : '';
    if (!size) {
      result.classList.add('is-waiting');
      result.textContent = this.#height > -1 ? this.dataset.textWaitWeight || '' : this.dataset.textWait || '';
      apply.disabled = true;
      delete apply.dataset.size;
      return;
    }
    result.classList.remove('is-waiting');
    result.textContent = size;
    const control = findSizeControl(size);
    apply.disabled = !control;
    apply.dataset.size = size;
    const template = control?.soldOut ? this.dataset.textApplySoldOut : this.dataset.textApply;
    apply.textContent = (template || '[size]').replace('[size]', size);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const tab = target.closest('[data-tab]');
    if (tab) {
      this.showTab(tab.getAttribute('data-tab') || 'finder');
      return;
    }
    const height = target.closest('[data-height]');
    if (height) {
      this.#height = Number(height.getAttribute('data-height'));
      this.#updateFinder();
      return;
    }
    const weight = target.closest('[data-weight]');
    if (weight) {
      this.#weight = Number(weight.getAttribute('data-weight'));
      this.#updateFinder();
      return;
    }
    const apply = target.closest('[data-finder-apply]');
    if (apply instanceof HTMLButtonElement && apply.dataset.size && !apply.disabled) {
      if (applySize(apply.dataset.size, this.#option)) {
        window.setTimeout(() => this.highlight(), 0);
        const drawer = this.closest(':is(theme-drawer, lucid-drawer)');
        if (drawer && typeof drawer.close === 'function') drawer.close();
      }
    }
  };

  #onTabKey = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const tabs = Array.from(this.querySelectorAll('[data-tab]'));
    const index = tabs.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    const next = tabs[(index + 1) % tabs.length];
    next.focus();
    this.showTab(next.getAttribute('data-tab') || 'finder');
  };

  #onRadioKey = (event) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    const group = event.currentTarget;
    const options = Array.from(group.querySelectorAll('[role="radio"]'));
    const index = options.indexOf(document.activeElement);
    if (index < 0) return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = options[(index + step + options.length) % options.length];
    next.focus();
    next.click();
  };
}

if (!customElements.get('lucid-size-guide')) customElements.define('lucid-size-guide', LucidSizeGuide);

/* Openers: any [data-lucid-size-guide] (value or data-lucid-size-guide-tab picks the tab: finder or table) and the
   size tools trigger [data-lucid-drawer-open="size-guide"], which the drawer shell itself opens. */
function onDocumentClick(event) {
  if (!(event.target instanceof Element) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
  const opener = event.target.closest(`[data-lucid-size-guide], [data-lucid-drawer-open^="${DRAWER_ID}"]`);
  if (!opener) return;
  const target = opener.getAttribute('data-lucid-drawer-open') || opener.getAttribute('data-lucid-size-guide-drawer') || DRAWER_ID;
  if (!isGuideId(target)) return;
  const drawer = findDrawer(target);
  if (!drawer) return;
  const tab = opener.getAttribute('data-lucid-size-guide-tab') || opener.getAttribute('data-lucid-size-guide') || '';
  pendingTab = tab === 'finder' || tab === 'table' ? tab : '';
  if (opener.hasAttribute('data-lucid-drawer-open')) return;
  event.preventDefault();
  if (drawer.isOpen) {
    drawer.querySelector('lucid-size-guide')?.showTab(pendingTab || 'finder');
    pendingTab = '';
    return;
  }
  if (typeof drawer.open === 'function') drawer.open();
}

document.addEventListener('click', onDocumentClick, true);
on('size-guide:open', (event) => {
  const tab = event.detail?.tab;
  if (tab === 'finder' || tab === 'table') pendingTab = tab;
});
